// src/mis_components/BounceClearStatus.jsx

import React, { useEffect, useState, useCallback, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import jwt_decode from "jwt-decode";
import { useAuth } from "../../../context/AuthContext";
import { trackFeatureAction } from "../../../analytics/googleAnalytics";
import { 
  fetchBounceClearStatus,
  uploadBounceClearStatus,
  downloadBounceClearStatus 
} from "../../../redux/slice/getOpsMISSlice";
import {BASE_URL} from '../../../redux/apiConfig'
import moment from "moment";
import {
  Spin,
  Table,
  Card,
  Input,
  Button,
  Select,
  Alert,
  Upload,
  Radio,
  Tooltip,
  message,
  Tag,
} from "antd";
import {
  SearchOutlined,
  CloseOutlined,
  UploadOutlined,
  FileExcelOutlined,
  InfoCircleOutlined,
  DownloadOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  ClockCircleOutlined,
  FileTextOutlined,
} from "@ant-design/icons";
import AsyncStorage from "@react-native-async-storage/async-storage";
import * as XLSX from "xlsx";

const { Option } = Select;

const BounceClearStatus = ({ extraProp }) => {
  const dispatch = useDispatch();
  const { authState, onLogout } = useAuth();

  // Local State
  const [loading, setLoading] = useState(true);
  const [searchField, setSearchField] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [isSearchActive, setIsSearchActive] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [currentLoading, setCurrentLoading] = useState(false);
  
  // Upload States
  const [fileList, setFileList] = useState([]);
  const [userUploadType, setUserUploadType] = useState(null);
  const [buttonLoading, setButtonLoading] = useState(false);
  const [uploadSuccess, setUploadSuccess] = useState(false);
  const [uploadFailedError, setUploadFailedError] = useState(false);
  const [invalidFormatError, setInvalidFormatError] = useState(false);
  const [branchFieldError, setBranchFieldError] = useState(false);
  const [uploadErrorMessage, setUploadErrorMessage] = useState("");

  // Download states
  const [downloadLoading, setDownloadLoading] = useState({
    Bounce_Clear_Status: false
  });

  // Memoize JWT parsing
  const jwtData = useMemo(() => {
    const jwtBearer = authState?.token;
    if (!jwtBearer) return null;

    try {
      const jwt_payload = jwt_decode(jwtBearer);
      return {
        jwtBearer,
        jwt_payload,
        jwt_payload_expiry: jwt_payload.exp,
        jwt_user_id: jwt_payload.user_id,
        jwt_user_name: extraProp?.username,
        jwt_payload_group: jwt_payload.group_id || jwt_payload.group
      };
    } catch (error) {
      console.error("JWT decode error:", error);
      return null;
    }
  }, [authState?.token, extraProp?.username]);

  // Redux State
  const {
    bounceClearData = [],
    bounceClearLoading,
    bounceClearError,
    paginationDataBounceClear = {},
    bounceClearLatestUpdated,
    bounceClearSummary,
    tokenExpired,
  } = useSelector((state) => ({
    bounceClearData: state.opsMIS?.bounceClearData || [],
    bounceClearLoading: state.opsMIS?.bounceClearLoading || false,
    bounceClearError: state.opsMIS?.bounceClearError || null,
    paginationDataBounceClear: state.opsMIS?.paginationDataBounceClear || {},
    bounceClearLatestUpdated: state.opsMIS?.bounceClearLatestUpdated || null,
    bounceClearSummary: state.opsMIS?.bounceClearSummary || null,
    tokenExpired: state.opsMIS?.data?.detail || ""
  }));

  // Upload state from Redux
  const { 
    uploadLoading, 
    uploadError, 
    uploadSuccess: reduxUploadSuccess 
  } = useSelector((state) => ({
    uploadLoading: state.uploadOpsMIS?.loading || false,
    uploadError: state.uploadOpsMIS?.error || null,
    uploadSuccess: state.uploadOpsMIS?.success || false
  }));

  // Handle Token Expiry
  const handleTokenExpiry = useCallback(async () => {
    const TOKEN_KEY = "my-jwt";
    try {
      await AsyncStorage.removeItem(TOKEN_KEY);
      onLogout();
      location.reload();
    } catch (error) {
      console.error("Error removing token:", error);
    }
  }, [onLogout]);

  // Check token expiry
  useEffect(() => {
    if (jwtData) {
      const currentTime = Math.floor(Date.now() / 1000);
      if (jwtData.jwt_payload_expiry && currentTime > jwtData.jwt_payload_expiry) {
        handleTokenExpiry();
        return;
      }
    }

    if (tokenExpired === "Signature has expired.") {
      handleTokenExpiry();
      return;
    }
  }, [jwtData, tokenExpired, handleTokenExpiry]);

  // Search Configuration
  const searchConfig = useMemo(() => ({
    searchFields: [
      { value: "branch_code", label: "Branch Code", param: "branch_code" },
      { value: "branch_name", label: "Branch Name", param: "branch_name" },
      { value: "business_associate_id", label: "Business Associate ID", param: "business_associate_id" },
      { value: "business_associate", label: "Business Associate", param: "business_associate" },
      { value: "collector_id", label: "Collector ID", param: "collector_id" },
      { value: "collector_name", label: "Collector Name", param: "collector_name" },
      { value: "loan_external_id", label: "Loan External ID", param: "loan_external_id" },
      { value: "name", label: "Customer Name", param: "name" },
      { value: "applicant_phone_no", label: "Applicant Phone", param: "applicant_phone_no" },
      { value: "product", label: "Product", param: "product" },
      { value: "loan_account_number", label: "Loan Account Number", param: "loan_account_number" },
      { value: "installment", label: "Installment", param: "installment" },
      { value: "status", label: "Status", param: "status" },
      { value: "umrn", label: "UMRN", param: "umrn" },
    ],
    placeholder: "Search by selected field..."
  }), []);

  // Fetch Data Function
  const fetchData = useCallback(async (page = 1, limit = 10, searchParams = {}) => {
    if (!jwtData?.jwtBearer) return;

    try {
      const params = {
        jwtBearer: jwtData.jwtBearer,
        page,
        limit,
        ...searchParams
      };
      
      await dispatch(fetchBounceClearStatus(params));
      setLoading(false);
    } catch (error) {
      console.error("Error fetching bounce clear data:", error);
      setLoading(false);
    }
  }, [dispatch, jwtData?.jwtBearer]);

  // Initial data fetch
  useEffect(() => {
    if (jwtData?.jwtBearer) {
      fetchData(currentPage, pageSize);
    }
  }, [jwtData?.jwtBearer, currentPage, pageSize, fetchData]);

  // Handle Key Press (Enter)
  const handleKeyPress = useCallback((e) => {
    if (e.key === 'Enter') {
      handleSearchSubmit();
    }
  }, [handleSearchSubmit]);

  // Handle Search
  const handleSearchSubmit = useCallback(async () => {
    if (!searchQuery.trim() || !searchField) return;
    
    setCurrentLoading(true);
    
    try {
      const searchParam = searchConfig.searchFields
        .find(field => field.value === searchField)?.param;
      
      if (searchParam) {
        const searchParams = {
          [searchParam]: searchQuery
        };
        
        setCurrentPage(1);
        await fetchData(1, pageSize, searchParams);
        setIsSearchActive(true);
        trackFeatureAction("MIS", "Bounce & Clear", "search", { field: searchField,});
      }
    } catch (error) {
      console.error("Search error:", error);
    } finally {
      setCurrentLoading(false);
    }
  }, [searchQuery, searchField, searchConfig.searchFields, pageSize, fetchData]);

  // Reset Search
  const resetSearchFilters = useCallback(async () => {
    setSearchQuery("");
    setSearchField("");
    setIsSearchActive(false);
    setCurrentPage(1);
    await fetchData(1, pageSize);
    trackFeatureAction("MIS", "Bounce & Clear", "reset");
  }, [pageSize, fetchData]);

  // Handle Table Change
  const handleTableChange = useCallback((pagination) => {
    const { current, pageSize: newPageSize } = pagination;
    
    setCurrentPage(current);
    if (newPageSize !== pageSize) {
      setPageSize(newPageSize);
      setCurrentPage(1);
    }
    
    const searchParams = {};
    if (isSearchActive && searchField && searchQuery) {
      const searchParam = searchConfig.searchFields
        .find(field => field.value === searchField)?.param;
      if (searchParam) {
        searchParams[searchParam] = searchQuery;
      }
    }
    
    fetchData(
      newPageSize !== pageSize ? 1 : current, 
      newPageSize, 
      searchParams
    );
  }, [pageSize, isSearchActive, searchField, searchQuery, searchConfig.searchFields, fetchData]);

  // Download Report
  const downloadReport = async () => {
    if (!jwtData?.jwtBearer) {
      message.error("Authentication token not found");
      return;
    }

    setDownloadLoading(prev => ({ ...prev, Bounce_Clear_Status: true }));

    try {
      const response = await fetch(`${BASE_URL}mis/bounce-clear-status/download/`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${jwtData.jwtBearer}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Download failed');
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      
      const contentDisposition = response.headers.get('content-disposition');
      let filename = `Bounce_Clear_Status_${new Date().toISOString().split('T')[0]}.xlsx`;
      
      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename="(.+)"/);
        if (filenameMatch) {
          filename = filenameMatch[1];
        }
      }
      
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      trackFeatureAction("MIS", "Bounce & Clear", "download");
      
      message.success('Report downloaded successfully');

    } catch (error) {
      console.error("Download error:", error);
      message.error(`Failed to download report: ${error.message}`);
    } finally {
      setDownloadLoading(prev => ({ ...prev, Bounce_Clear_Status: false }));
    }
  };

  // Download Sample File
  const downloadSampleFile = () => {

    trackFeatureAction("MIS", "Bounce & Clear", "download_sample");

    const sampleData = [
      {
        'Branch Code': 10120,
        'Branch Name': 'Baraut',
        'Business Associate id': 'ZFT3621',
        'Business Associate': 'HARSH Tyagi',
        'Collector ID': 'ZFT3632',
        'Collector Name': 'ROHIT KUMAR',
        'Loan External Id': 100380401048320,
        'UMRN': 'SBIN7023009230092916',
        'Name': 'SACHIN DIXIT',
        'Customer Address': 'S/O Brahmpal Sharma, House No-12, Village-Gandhi, Gyasri Urf Gadhi, Baghpat',
        'Applicant phone no': 9760072550,
        'Bank Account Holder Name': 'Sachin dixit',
        'Disbursal Account': 38302405969,
        'IFSC Code': 'SBIN0013410',
        'Product': 'Pride Loan - Oxyzo Secure',
        'Loan Account Number': 'OXYTL01FZ9G',
        'Due Date': new Date(2026, 6, 3),
        'Installment': 33,
        'Principal Due': 8197.13,
        'Interest Due': 12886.22,
        'Fees Due': 0,
        'Penalty Due': 0,
        'Total Due': 21084,
        'EMI-Round Up': 21084,
        'Due Amt Presented': 20082.55,
        'Total Overdue': 20083.0,
        'Disbursal Date': new Date(2023, 9, 3),
        'Loan Amount': 900000,
        'MODE': 'NACH',
        'Partner': '_Oxyzo Financial Services Pvt Ltd',
        'Presentation Status': '_NACH Presented',
        'Status': '_Bounce',
        'Reason': 'BALANCE INSUFFICIENT',
        'EMI Flag': '_Non-OD'
      },
      {
        'Branch Code': 10083,
        'Branch Name': 'Rudrapur',
        'Business Associate id': 'ZFT2977',
        'Business Associate': 'Deepak Deepak',
        'Collector ID': '-',
        'Collector Name': '-',
        'Loan External Id': 100750101051757,
        'UMRN': 'BARB7022010230008819',
        'Name': 'ANKUR GUPTA',
        'Customer Address': 'S/O Om Prakash Gupta #49 WARD NO - 4 GANDHI NAGAR SULTANPUR PATTI',
        'Applicant phone no': 8077483607,
        'Bank Account Holder Name': 'ANKUR GUPTA',
        'Disbursal Account': 6560100013717,
        'IFSC Code': 'BARB0SULNAI',
        'Product': 'Pride Loan - Oxyzo Unsecure',
        'Loan Account Number': 'OXYTL0184NX',
        'Due Date': new Date(2026, 6, 3),
        'Installment': 32,
        'Principal Due': 7371.62,
        'Interest Due': 901.1,
        'Fees Due': 0,
        'Penalty Due': 0,
        'Total Due': 8273,
        'EMI-Round Up': 8273,
        'Due Amt Presented': 8273.0,
        'Total Overdue': 173124.54,
        'Disbursal Date': new Date(2023, 9, 20),
        'Loan Amount': 200000,
        'MODE': 'NACH',
        'Partner': '_Oxyzo Financial Services Pvt Ltd',
        'Presentation Status': '_NACH Presented',
        'Status': '_Clear',
        'Reason': 'Accepted Claim Amount',
        'EMI Flag': '_OD'
      }
    ];

    const ws = XLSX.utils.json_to_sheet(sampleData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Bounce_Clear_Status");
    
    const range = XLSX.utils.decode_range(ws['!ref']);
    for (let R = range.s.r + 1; R <= range.e.r; R++) {
      for (let C = range.s.c; C <= range.e.c; C++) {
        const cellAddress = XLSX.utils.encode_cell({ r: R, c: C });
        const cell = ws[cellAddress];
        if (cell && cell.t === 'd') {
          cell.z = 'yyyy-mm-dd';
        }
      }
    }

    const excelBuffer = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
    const blob = new Blob([excelBuffer], { 
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' 
    });
    
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `Bounce_Clear_Sample_File.xlsx`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
    
    message.success('Sample file downloaded successfully');
  };

  // Handle file change for upload
  const handleFileChange = ({ fileList }) => {
    setFileList(fileList);
    setUploadFailedError(false);
    setInvalidFormatError(false);
    setBranchFieldError(false);
    setUploadSuccess(false);
    setUploadErrorMessage("");
  };

  // Handle upload type change
  const onUploadTypeChange = (e) => {
    setUserUploadType(e.target.value);
  };

  // Handle file upload
  const handleUpload = async () => {
    const today = new Date();
    const currentDay = today.getDate();
    
    if (currentDay <= 4) {
      message.error(`Upload is allowed only after the 4th of the month. Current date: ${today.toLocaleDateString()}`);
      return;
    }

    if (fileList.length === 0 || !userUploadType) {
      message.error('Please select a file and upload type');
      return;
    }
    
    const file = fileList[0].originFileObj;
    const fileName = file.name;
    const fileExtension = fileName.split('.').pop().toLowerCase();
    
    if (fileExtension !== 'xlsx' && fileExtension !== 'xls') {
      setInvalidFormatError(true);
      return;
    }
    
    setButtonLoading(true);
    setUploadFailedError(false);
    setBranchFieldError(false);
    setUploadSuccess(false);
    setUploadErrorMessage("");
    
    try {
      const result = await dispatch(uploadBounceClearStatus({
        jwt_token: jwtData.jwtBearer,
        uploadType: userUploadType,
        file: file,
        month: moment().format('YYYY-MM')
      }));

      if (uploadBounceClearStatus.fulfilled.match(result)) {
        setUploadSuccess(true);
        setFileList([]);
        setUserUploadType(null);

        trackFeatureAction("MIS", "Bounce & Clear", "upload", { upload_type: userUploadType });

        message.success(`File uploaded successfully!`);
        fetchData(currentPage, pageSize);
      } else {
        setUploadFailedError(true);
        setUploadErrorMessage(result.payload || 'Upload failed');
        if (result.payload?.toLowerCase().includes('branch')) {
          setBranchFieldError(true);
        }
      }
    } catch (error) {
      console.error("Upload error:", error);
      setUploadFailedError(true);
      setUploadErrorMessage(error.message || 'Upload failed');
    } finally {
      setButtonLoading(false);
    }
  };

  // Custom request handler for Antd Upload
  const customRequest = ({ file, onSuccess }) => {
    setTimeout(() => {
      onSuccess("ok");
    }, 0);
  };

  // Generate Columns with status tags
  const columns = useMemo(() => {
    if (!bounceClearData?.length) return [];

    const allKeys = bounceClearData.reduce((keys, record) => {
      return keys.concat(Object.keys(record));
    }, []);

    const uniqueKeys = [...new Set(allKeys)];

    const baseColumns = uniqueKeys.map((key) => {
      const isStatusColumn = key.toLowerCase().includes('status');
      
      return {
        title: <strong>{key.toUpperCase().replace(/_/g, ' ')}</strong>,
        dataIndex: key,
        key: key,
        width: isStatusColumn ? "15%" : "30%",
        align: "left",
        render: (text) => {
          if (isStatusColumn && text) {
            const normalized = text.toLowerCase().replace(/^_/, '');
            
            const statusMap = {
              'bounce': { color: 'error', icon: <CloseCircleOutlined />, label: 'Bounced' },
              'bounced': { color: 'error', icon: <CloseCircleOutlined />, label: 'Bounced' },
              'clear': { color: 'success', icon: <CheckCircleOutlined />, label: 'Cleared' },
              'cleared': { color: 'success', icon: <CheckCircleOutlined />, label: 'Cleared' },
              'pending': { color: 'warning', icon: <ClockCircleOutlined />, label: 'Pending' },
            };
            
            let status = null;
            for (const [key, value] of Object.entries(statusMap)) {
              if (normalized.includes(key)) {
                status = value;
                break;
              }
            }
            
            if (status) {
              return (
                <Tag color={status.color} icon={status.icon}>
                  {status.label}
                </Tag>
              );
            }
            return <Tag>{text}</Tag>;
          }
          return text || '-';
        }
      };
    });

    return baseColumns;
  }, [bounceClearData]);

  // Pagination Options
  const paginationOptions = useMemo(() => ({
    current: currentPage,
    pageSize: pageSize,
    total: paginationDataBounceClear?.total_count || 0,
    showSizeChanger: true,
    pageSizeOptions: ["10", "20", "50", "100"],
    showQuickJumper: true,
    showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} items`,
    onChange: (page, size) => {
      handleTableChange({ current: page, pageSize: size });
    },
    onShowSizeChange: (current, size) => {
      handleTableChange({ current: 1, pageSize: size });
    }
  }), [currentPage, pageSize, paginationDataBounceClear?.total_count, handleTableChange]);

  // Check if user has upload permission (same as expected collection)
  const canUpload = useMemo(() => {
    const groupId = jwtData?.jwt_payload_group;
    return groupId && !["0", "1", "2", "3", "4", "13", "19"].includes(groupId.toString());
  }, [jwtData?.jwt_payload_group]);

  // Format date for display
  const formatLastUpdated = useMemo(() => {
    if (!bounceClearLatestUpdated) return null;
    try {
      const date = new Date(bounceClearLatestUpdated);
      return {
        date: moment(date).add(5, 'hours').add(30, 'minutes').format('DD-MM-YYYY'),
        time: moment(date).add(5, 'hours').add(30, 'minutes').format('HH:mm')
      };
    } catch {
      return null;
    }
  }, [bounceClearLatestUpdated]);

  if (!jwtData) {
    return (
      <div className="spinner-container" style={{ textAlign: "center", padding: "50px" }}>
        <Spin size="large" />
      </div>
    );
  }

  return (
    <center>
      {/* Upload Card - Conditionally rendered based on user group_id (same as expected collection) */}
      {canUpload && (
        <Card
          style={{
            width: "60%",
            marginTop: "20px",
            boxShadow: "0 4px 8px 0 rgba(0, 0, 0, 0.2)",
            borderRadius: "5px",
          }}
          bordered={true}
        >
          <center>
            <div>
              <p>
                <strong>
                  <FileExcelOutlined /> UPLOAD BOUNCE & CLEAR STATUS REPORT
                </strong>
              </p>
              <div style={{ display: "flex", justifyContent: "center" }}>
                <Upload
                  customRequest={customRequest}
                  name="file"
                  fileList={fileList}
                  listType="picture-card"
                  onChange={handleFileChange}
                  maxCount={1}
                  style={{ alignItems: "center" }}
                  beforeUpload={() => false}
                >
                  {fileList.length === 0 && (
                    <div>
                      <UploadOutlined />
                      <div style={{ marginTop: 8 }}>Select File</div>
                    </div>
                  )}
                </Upload>
              </div>
              <div>
                <p>
                  <strong>Select upload type</strong>
                  <Tooltip
                    title={
                      <span>
                        Fresh -
                        <br />
                        Selecting fresh as upload type will delete the
                        previous data and show the new data.
                        <br /> <br />
                        Append -
                        <br />
                        Selecting append as upload type will add the new
                        data along with the old data.
                      </span>
                    }
                  >
                    <InfoCircleOutlined
                      style={{
                        marginLeft: "5px",
                        cursor: "pointer",
                        color: "dodgerblue",
                        fontSize: "16px",
                      }}
                    />
                  </Tooltip>
                </p>
                <Radio.Group
                  onChange={onUploadTypeChange}
                  value={userUploadType}
                >
                  <Radio value={"Fresh"}>Fresh</Radio>
                  <Radio value={"Append"}>Append</Radio>
                </Radio.Group>
              </div>
              <Button
                className="regular-button"
                style={{
                  color: "white",
                  padding: "10px 20px",
                  borderRadius: "5px",
                  display: "inline-flex",
                  alignItems: "center",
                  fontWeight: "bold",
                  marginTop: "20px",
                }}
                onClick={handleUpload}
                loading={buttonLoading}
                disabled={fileList.length === 0 || userUploadType === null}
              >
                Upload
              </Button>
              {branchFieldError === false ? null : (
                <Alert
                  style={{
                    marginTop: "20px",
                    textAlign: "justify",
                    width: "50%",
                  }}
                  message="Error"
                  description="Branch Code or Branch Name field is missing from the excel sheet."
                  type="error"
                  showIcon
                />
              )}
              {uploadFailedError === false ? null : (
                <Alert
                  style={{
                    marginTop: "20px",
                    textAlign: "justify",
                    width: "50%",
                  }}
                  message="Error"
                  description={uploadErrorMessage || "File Upload Failed."}
                  type="error"
                  showIcon
                />
              )}
              {invalidFormatError === false ? null : (
                <Alert
                  style={{
                    marginTop: "20px",
                    textAlign: "justify",
                    width: "50%",
                  }}
                  message="Error"
                  description="Invalid file format. Allowed formats: xlsx, xls"
                  type="error"
                  showIcon
                />
              )}
              {uploadSuccess === false ? null : (
                <Alert
                  style={{
                    marginTop: "20px",
                    textAlign: "justify",
                    width: "50%",
                  }}
                  message="Success"
                  description="File Uploaded Successfully"
                  type="success"
                  showIcon
                />
              )}
              <div
                style={{
                  textAlign: "justify",
                }}
              >
                <p>
                  <strong>Instructions to be followed -</strong>
                </p>
                <p>1. The extension of the file should be 'xlsx' or 'xls'.</p>
                <p>
                  2. Name of one of the column of the excel file should be
                  'Branch Code' and it should have data in either of the below
                  format - '10001-Jobner' or '10001'.
                </p>
                <p>
                  3. The excel sheet should not be having any formula in any of
                  the cells and cells should only have values in it.
                </p>
                <p>4. Upload is allowed only <strong>after the 4th of every month</strong>.</p>
                <p>
                  5. For reference, download the sample file below to understand the expected format.
                </p>
                <div style={{ textAlign: "center", marginTop: "15px" }}>
                  <Button
                    type="primary"
                    icon={<FileTextOutlined />}
                    onClick={downloadSampleFile}
                    style={{
                      background: "linear-gradient(45deg, #2196F3, #21CBF3)",
                      border: "none",
                      color: "white",
                    }}
                  >
                    Download Sample File
                  </Button>
                </div>
              </div>
            </div>
          </center>
        </Card>
      )}

      {/* Main Data Card - Same styling as Expected Collection */}
      <Card
        layout="horizontal"
        bordered={true}
        className="criclebox tablespace mb-24"
        style={{ width: "95%", marginTop: "20px" }}
        title={
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", width: "100%" }}>
            {/* Search Container - Left */}
            <div>
              <div style={{
                display: "flex",
                alignItems: "center",
                gap: "10px",
                flexWrap: "wrap"
              }}>
                {/* Search Field Selector */}
                <Select
                  value={searchField}
                  onChange={setSearchField}
                  style={{
                    width: "180px",
                    height: "44px",
                    borderRadius: "8px",
                    border: "1px solid #e0e6ed",
                    backgroundColor: "#fff",
                    boxShadow: "0 2px 6px rgba(0, 0, 0, 0.03)",
                    fontWeight: "500",
                    marginRight: "10px"
                  }}
                  placeholder="Select field"
                  dropdownStyle={{
                    borderRadius: "8px",
                    padding: "8px",
                    boxShadow: "0 8px 24px rgba(0, 0, 0, 0.12)"
                  }}
                  size="middle"
                >
                  {searchConfig.searchFields.map((field) => (
                    <Option 
                      key={field.value} 
                      value={field.value}
                      style={{
                        padding: "10px 12px",
                        borderRadius: "6px",
                        margin: "4px 0",
                        transition: "all 0.2s ease"
                      }}
                    >
                      {field.label}
                    </Option>
                  ))}
                </Select>

                {/* Search Input */}
                <Input
                  placeholder={searchConfig.placeholder}
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  onKeyPress={handleKeyPress}
                  allowClear
                  style={{
                    width: "300px",
                    height: "44px",
                    borderRadius: "8px",
                    border: "1px solid #e0e6ed",
                    padding: "0 16px",
                    fontSize: "14px",
                    boxShadow: "0 2px 6px rgba(0, 0, 0, 0.03)",
                    transition: "all 0.3s ease",
                    marginRight: "10px"
                  }}
                />

                {/* Search Button */}
                <Button
                  type="primary"
                  icon={<SearchOutlined />}
                  onClick={handleSearchSubmit}
                  loading={currentLoading}
                  disabled={!searchQuery.trim() || !searchField}
                  style={{
                    height: "44px",
                    padding: "0 20px",
                    borderRadius: "8px",
                    fontWeight: "500",
                    border: "none",
                    boxShadow: "0 2px 8px rgba(58, 123, 213, 0.3)",
                    transition: "all 0.3s ease",
                    display: "flex",
                    alignItems: "center",
                    gap: "6px",
                    marginRight: "10px"
                  }}
                >
                  Search
                </Button>

                {/* Reset Button */}
                {isSearchActive && (
                  <Button
                    icon={<CloseOutlined />}
                    onClick={resetSearchFilters}
                    style={{
                      height: "44px",
                      padding: "0 16px",
                      borderRadius: "8px",
                      fontWeight: "500",
                      color: "#ff4d4f",
                      borderColor: "rgba(255, 77, 79, 0.3)",
                      background: "rgba(255, 77, 79, 0.08)",
                      transition: "all 0.3s ease"
                    }}
                  >
                    Reset
                  </Button>
                )}
              </div>
            </div>

            {/* Title - Center */}
            <div style={{ 
              flex: "1 1 auto", 
              textAlign: "center", 
              fontSize: "18px", 
              fontWeight: "bold" 
            }}>
              BOUNCE & CLEAR STATUS TRACKER
              <span style={{ marginLeft: "10px", fontSize: "14px" }}>
                <br />
                <strong>DATE</strong> - {formatLastUpdated?.date || 'N/A'} &nbsp;
                <strong>TIME</strong> - {formatLastUpdated?.time || 'N/A'} (24 HRS)
              </span>
            </div>

            {/* Download Buttons - Right */}
            <div style={{
              display: "flex",
              gap: "12px",
              flex: "0 0 auto"
            }}>
              <Button
                type="primary"
                icon={<DownloadOutlined />}
                onClick={downloadReport}
                loading={downloadLoading.Bounce_Clear_Status}
                style={{
                  height: "40px",
                  padding: "0 16px",
                  borderRadius: "6px",
                  fontWeight: "500",
                  display: "flex",
                  alignItems: "center",
                  gap: "6px",
                  background: "linear-gradient(45deg, #ff512f, #dd2476)",
                  border: "none"
                }}
              >
                Download Report
              </Button>
            </div>
          </div>
        }
      >
        <div className="table-responsive">
          {bounceClearLoading && !currentLoading ? (
            <div className="spinner-container">
              <Spin size="large" />
            </div>
          ) : bounceClearError ? (
            <Alert
              message="Error"
              description="Failed to load bounce clear data. Please try again."
              type="error"
              showIcon
            />
          ) : bounceClearData.length === 0 ? (
            <Alert
              message="No Data Available"
              description="No bounce & clear records found. Please upload the MTD file after 4th of the month."
              type="info"
              showIcon
            />
          ) : (
            <Table
              columns={columns}
              dataSource={bounceClearData}
              pagination={paginationOptions}
              bordered
              rowClassName={(record, index) =>
                index % 2 === 0 ? "table-row-light" : "table-row-dark"
              }
              loading={bounceClearLoading || currentLoading}
              rowKey={(record, index) => record.id || index}
            />
          )}
        </div>
      </Card>
    </center>
  );
};

export default BounceClearStatus;